import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { IPortfolioItemListItem, Maybe } from 'graphql/types';
import { formatDate } from 'common/utils/formatDate';
import { receivedCertificateEndDateNear } from 'common/utils/receivedCertificate';

type TSkillPortfolioItem = Pick<
    IPortfolioItemListItem,
    | 'progress'
    | 'score'
    | 'scoreThreshold'
    | 'endDate'
    | 'completionDate'
    | 'isCompliant'
>;

export const useSkillStatusDescription = (
    portfolioItem?: Maybe<TSkillPortfolioItem>
) => {
    const [translate] = useTranslation();

    return useMemo(() => {
        // No portfolio item means the skill has not started yet.
        if (!portfolioItem) {
            return translate('skillCard.noProgress');
        }

        const {
            progress,
            score,
            scoreThreshold,
            endDate,
            completionDate,
            isCompliant,
        } = portfolioItem;

        // Compliant means all skill requirements are met
        // so show the certificate validity or completion state.
        if (!!isCompliant) {
            if (!!endDate) {
                return translate('skillCard.validUntil', {
                    date: formatDate(endDate, 'dd MMMM yyyy'),
                });
            }

            return translate('skillCard.completed');
        }

        // Explicit `false` means the skill requirements are not met
        if (isCompliant === false) {
            return translate('skillCard.notCertified');
        }

        // `isCompliant` is nullish from here on: fall back to deriving status
        // from progress, score, and dates.

        // Active progress on a (re-)certification attempt takes precedence
        // over an expired endDate: report the percentage instead of "not
        // certified". Matches SkillProgressChip.
        if (progress && progress < 1 && !completionDate) {
            const percentage = Math.round(progress * 100);

            return translate('skillCard.progress', { percentage });
        }

        // Certificate has expired — no longer certified, regardless of any
        // remaining progress or score. Matches SkillProgressChip's grey
        // fallback for the same case.
        if (endDate && new Date(endDate) < new Date()) {
            return translate('skillCard.notCertified');
        }

        // Fully completed but the certificate is close to expiring
        if (
            endDate &&
            receivedCertificateEndDateNear(endDate) &&
            progress === 1
        ) {
            return translate('skillCard.expiresOn', {
                date: formatDate(endDate, 'dd MMMM yyyy'),
            });
        }

        // Still in progress: not yet 100% and no completion date recorded.
        if (progress !== 1 && !completionDate) {
            // No progress at all — treat the same as an unstarted skill.
            if (!progress) return translate('skillCard.noProgress');

            const percentage = Math.round(progress * 100);

            return translate('skillCard.progress', { percentage });
        }

        // Completed the content but failed to meet the required score
        // threshold, so the skill is not certified.
        if (
            scoreThreshold &&
            score !== undefined &&
            score !== null &&
            score < scoreThreshold
        ) {
            return translate('skillCard.notCertified');
        }

        // Completed successfully with a future expiry date.
        if (endDate) {
            return translate('skillCard.validUntil', {
                date: formatDate(endDate, 'dd MMMM yyyy'),
            });
        }

        // Completed successfully with no expiry date.
        return translate('skillCard.completed');
    }, [portfolioItem, translate]);
};
